import type { ButtonHTMLAttributes } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  busy?: boolean;
  busyLabel?: string;
}

// Every submit-style button in the app should use this component so the
// "disabled + relabeled while busy" immediate-feedback pattern (§5.1 of the
// v6 architecture doc) is applied once, centrally, instead of per-screen.
export function Button({ variant = 'primary', busy, busyLabel, children, disabled, className = '', ...rest }: ButtonProps) {
  const base =
    'inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold tracking-tight transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100';
  const variants = {
    primary: 'border border-gold-300/60 bg-gold-gradient text-ink shadow-gold hover:brightness-105',
    secondary: 'border border-ink-border bg-ink-raised text-slate-100 hover:border-gold-700 hover:bg-ink-overlay',
    danger: 'border border-danger/40 bg-danger/10 text-red-200 hover:bg-danger/20',
    ghost: 'border border-transparent text-slate-300 hover:bg-white/5',
  };

  return (
    <button className={`${base} ${variants[variant]} ${className}`} disabled={disabled || busy} {...rest}>
      {busy && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />}
      {busy && busyLabel ? busyLabel : children}
    </button>
  );
}
